import { Component, Element, Prop, h } from '@stencil/core';

import { onTwoKeysModsInteraction } from '../actions/actions.answers';
import { actions as pageParamsActions } from '../actions/actions.page-params';
import { onQuestionsInit } from '../actions/actions.questions';
import { state as afterMarketState, onChange as onAftermarketPropChange, on as onAftermarketStateChange } from '../store/store.aftermarket';
import { state as answerState, on as onAnswerStateChange } from '../store/store.answers';
import { state as dynamicEntryState } from '../store/store.dynamic-entry';
import { on as onValidationStateChange, state as validationState } from '../store/store.validation';
import { sendModificationResponses } from '../tracking/tracking.conditions';
import { trackKeysModificationsLoad } from '../tracking/tracking.keysModifications';

@Component({
  tag: 'keys-modifications-state-hydrator',
  shadow: true,
})
export class KeysModificationsStateHydrator {
  @Element() el: HTMLElement;
  @Prop() colorId: number = 0;
  @Prop() drivetrainId: number = 0;
  @Prop() drivetrain: string = '';
  @Prop() engineId: number = 0;
  @Prop() modalPositionOffsetTop: string = '';
  @Prop() makeId: number = 0;
  @Prop() mileage: number = 0;
  @Prop() modelId: number = 0;
  @Prop() transmissionId: number = 0;
  @Prop() trimId: number = 0;
  @Prop() vin: string = '';
  @Prop() yearId: number = 0;

  // Analytics
  @Prop() entry: string;
  @Prop() offerCode: string;
  @Prop() lnxCode: string;
  @Prop() category: string;
  @Prop() theme: string;

  @Prop() showTwoKeysQuestion: boolean = false;
  @Prop() allowIneligibleVehicleSubmission: boolean = false;

  async componentWillLoad() {
    pageParamsActions.onReceivedParams({ entry: this.entry, offerCode: this.offerCode, lnxCode: this.lnxCode, category: this.category });

    await onQuestionsInit(
      {
        yearId: this.yearId,
        makeId: this.makeId,
        modelId: this.modelId,
        trimId: this.trimId,
        colorId: this.colorId,
        transmissionId: this.transmissionId,
        engineId: this.engineId,
        drivetrainId: this.drivetrainId,
        drivetrain: this.drivetrain,
        vin: this.vin,
        mileage: this.mileage,
      },
      this.allowIneligibleVehicleSubmission
    );

    this.setupStateProps();

    onAnswerStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);

      if (prop === 'history') {
        this.updateStateProps('keys', value);
      } else if (prop === 'conditions') {
        this.updateStateProps('modifications', value);
      }
    });

    onAftermarketStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onValidationStateChange('set', (prop, value) => {
      if (prop === 'form') {
        this.updateStateProps('formErrors', value.keysModifications);
      }
    });
    trackKeysModificationsLoad();
    onTwoKeysModsInteraction();

    onAftermarketPropChange('userInteraction', sendModificationResponses);
  }

  // updates only the changed prop
  updateStateProps(prop, value) {
    this.el.childNodes.forEach((element) => {
      element[prop] = value;
    });
  }

  // explicitly declare initial state props
  setupStateProps() {
    const allowIneligibleVehicleSubmission = this.allowIneligibleVehicleSubmission || dynamicEntryState.allowIneligibleVehicleSubmission;
    this.el.childNodes.forEach((element) => {
      // Two keys and vehicle color answered
      element['showTwoKeysQuestion'] = this.showTwoKeysQuestion;
      element['questionsKey'] = afterMarketState.twoKeysQuestions;
      element['allowIneligibleVehicleSubmission'] = allowIneligibleVehicleSubmission;

      // Aftermarket
      element['questionsAftermarket'] = afterMarketState.questionsAftermarket;
      element['questionsExhaust'] = afterMarketState.exhaustQuestions;
      element['questionsStereo'] = afterMarketState.stereoQuestions;
      element['questionsWheelsTires'] = afterMarketState.wheelsTiresQuestions;
      element['questionsSuspension'] = afterMarketState.suspensionQuestions;
      element['questionsPerformance'] = afterMarketState.performanceQuestions;

      // Toggle question
      element['questionsToggleModifications'] = afterMarketState.toggleModificationsQuestion;
      element['showModificationsQuestion'] = afterMarketState.showModificationsQuestion;
      element['showModificationsQuestionGroup'] = afterMarketState.showModificationsQuestionGroup;
      element['showModificationRemovalPrompt'] = afterMarketState.showModificationRemovalPrompt;

      // Answers
      element['keys'] = answerState.history;
      element['modifications'] = answerState.conditions;

      // Validation
      element['formErrors'] = validationState.form.keysModifications;

      // theme
      element['theme'] = this.theme;
      element['modalPositionOffsetTop'] = this.modalPositionOffsetTop;

      // Interaction tracking
      element['userInteraction'] = afterMarketState.userInteraction;
    });
  }

  render() {
    return (
      <div class="hydration-state hydration-state--conditions">
        <slot />
      </div>
    );
  }
}
